import React from 'react';
import { FileUp, Upload, X } from 'lucide-react';
import { DOCUMENT_UPLOAD_TYPES } from '../../../constants';
import { useDragAndDrop } from '../../../hooks';
import { Button } from '../../ui/button';
import { cn } from '../../ui/utils';

const DEFAULT_ACCEPTED_TYPES = DOCUMENT_UPLOAD_TYPES;

const toAcceptAttribute = (types) =>
  types.map((type) => (type.startsWith('.') ? type : `.${type}`)).join(',');

const FileSelectInput = ({
  fileInputRef,
  triggerFileSelect,
  selectedFile,
  acceptedFileTypes,
  maxFileSize,
  handleFileSelect,
  handleFileRemoved,
  handleFilePickerCancel
}) => {
  const resolvedAcceptedTypes =
    acceptedFileTypes?.length > 0 ? acceptedFileTypes : DEFAULT_ACCEPTED_TYPES;

  const { isDragging, handleDragEnter, handleDragOver, handleDragLeave, handleDrop } =
    useDragAndDrop({
      acceptedFileTypes: resolvedAcceptedTypes,
      handleFileSelect
    });

  const openFilePicker = () => {
    triggerFileSelect();
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  return (
    <div className="mt-3 w-full">
      <input
        type="file"
        ref={fileInputRef}
        className="sr-only"
        onChange={handleFileSelect}
        onCancel={handleFilePickerCancel}
        accept={toAcceptAttribute(resolvedAcceptedTypes)}
        tabIndex={-1}
      />

      <div
        id="file-select-input-container"
        role="button"
        tabIndex={0}
        aria-label="Upload document. Drag and drop a file here, or click to browse."
        onClick={openFilePicker}
        onKeyDown={onKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'group relative flex w-full cursor-pointer flex-col items-center justify-center gap-3',
          'rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-all duration-200',
          'outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2',
          isDragging
            ? 'border-primary bg-primary/10 shadow-sm'
            : 'border-border bg-muted/20 hover:border-primary/50 hover:bg-primary/5'
        )}
      >
        <div
          className={cn(
            'flex h-14 w-14 items-center justify-center rounded-2xl border transition-colors',
            isDragging
              ? 'border-primary/30 bg-primary/15 text-primary'
              : 'border-border/70 bg-white text-muted-foreground group-hover:border-primary/30 group-hover:text-primary'
          )}
        >
          {isDragging ? <Upload className="h-6 w-6" /> : <FileUp className="h-6 w-6" />}
        </div>

        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">
            {isDragging ? 'Drop your file here' : 'Drag & drop your file here'}
          </p>
          <p className="text-xs text-muted-foreground">or click anywhere in this area to browse</p>
        </div>

        {selectedFile && (
          <div
            className="flex max-w-full items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2"
            onClick={(event) => event.stopPropagation()}
          >
            <p className="truncate text-xs font-medium text-primary" title={selectedFile.name}>
              {selectedFile.name}
            </p>
            <button
              type="button"
              aria-label="Remove selected file"
              onClick={(event) => {
                event.stopPropagation();
                handleFileRemoved();
              }}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white hover:text-rose-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="pointer-events-none mt-1 border-border bg-white"
          tabIndex={-1}
        >
          Browse Files
        </Button>

        <p className="text-[11px] leading-relaxed text-muted-foreground">
          Accepted formats: {resolvedAcceptedTypes.join(', ').toUpperCase()} · Max size:{' '}
          {maxFileSize || 10} MB
        </p>
      </div>
    </div>
  );
};

export default FileSelectInput;
